import React from "react"
import styles from './styles.module.css'
import {
    BlockedNumber,
    BlockStatus,
    Evidence,
    statusLabel,
} from "../../models/RobocallBlocks/BlockedNumber"

/**
 * The small read-only pieces every row and the detail page share.
 *
 * The point of §8a is that a reviewer decides from the row alone, without running a
 * query — so the evidence has to be legible at a glance, and anything that weakens it
 * (too few duration samples, a veto that contradicts the signature) has to be visible
 * rather than implied.
 */

export const ScoreBadge = (props: { score: number }) => {
    const cls = props.score >= 70
        ? styles.ScoreHigh
        : (props.score >= 40 ? styles.ScoreMid : styles.ScoreLow)
    return <span className={`${styles.Score} ${cls}`}>{props.score}</span>
}

export const StatusBadge = (props: { status: BlockStatus }) => {
    const map: { [k: string]: string } = {
        candidate: styles.StatusCandidate,
        active: styles.StatusActive,
        released: styles.StatusReleased,
        expired: styles.StatusExpired,
    }
    return (
        <span className={`${styles.Status} ${map[props.status] || ''}`}>
            {statusLabel(props.status)}
        </span>
    )
}

/**
 * Veto strength, spelled out. A weak veto is the case that matters: one stray Booked tag
 * on a number with a textbook robocall signature. Automation cannot promote it, so if the
 * reviewer cannot see it, nobody ever resolves it.
 */
export const VetoBadge = (props: { number: BlockedNumber }) => {
    const strength = props.number.veto_strength || 0
    if (strength <= 0) {
        return null
    }
    const terminal = props.number.veto_protected || strength >= 2
    return (
        <span
            className={`${styles.Veto} ${terminal ? styles.VetoStrong : styles.VetoWeak}`}
            title={terminal
                ? 'Terminal veto: two or more pieces of real-lead evidence. Scoring will never block this number.'
                : 'Weak veto: one piece of real-lead evidence. Automation cannot promote it, but you can.'}
        >
            {terminal ? `VETO ×${strength}` : `veto ×1 (weak)`}
        </span>
    )
}

const num = (v: any, fallback: string = '—'): string =>
    (v === undefined || v === null) ? fallback : String(v)

/**
 * The evidence summary, in the order a reviewer actually reads it: how wide the sweep
 * was, how short the calls were, and what agents said about it.
 */
export const EvidenceSummary = (props: { evidence?: Evidence | null, minSamples?: number }) => {
    const e = props.evidence
    if (!e) {
        return <span className={styles.EvidenceMuted}>no evidence recorded</span>
    }

    const samples = e.duration_samples
    const minSamples = props.minSamples === undefined ? 3 : props.minSamples
    const thinMedian = samples !== undefined && samples !== null && samples < minSamples

    return (
        <div className={styles.Evidence}>
            <div>
                <b>{num(e.distinct_businesses)}</b> businesses
                {' · '}
                <b>{num(e.calls)}</b> calls
                {' · '}
                <b>{num(e.answered_calls)}</b> answered
            </div>
            <div className={thinMedian ? styles.EvidenceWarn : undefined}>
                median{' '}
                <b>{e.median_handle_s === null || e.median_handle_s === undefined
                    ? '—'
                    : `${e.median_handle_s}s`}</b>
                {' over '}
                {num(samples)} sample{samples === 1 ? '' : 's'}
                {thinMedian ? ` (below ${minSamples} — not enough to block on)` : ''}
            </div>
            <div className={styles.EvidenceMuted}>
                spam tags: {num(e.strong_spam_tags, '0')} strong / {num(e.weak_spam_tags, '0')} weak
                {' · '}
                lead tags: {num(e.lead_tags, '0')}
                {' · '}
                long convos: {num(e.long_convos, '0')}
            </div>
        </div>
    )
}

/** Which businesses it hit — the fastest way to recognise a regional directory walk. */
export const BusinessesSample = (props: { evidence?: Evidence | null }) => {
    const sample = props.evidence?.businesses_sample
    if (!sample || sample.length === 0) {
        return null
    }
    return (
        <div className={styles.Sample} title="Businesses this number contacted (sample)">
            {sample.slice(0, 8).join(', ')}{sample.length > 8 ? ' …' : ''}
        </div>
    )
}

export const KeyValue = (props: { label: string, children?: React.ReactNode }) => (
    <div className={styles.KeyValue}><span>{props.label}: </span>{props.children}</div>
)
